/**
 * Silent SSO via hidden iframe — OIDC `prompt=none` approach.
 *
 * ⚠️ NOT WIRED — PARKED (ADR-0015 §6). Nothing calls `attemptSilentAuth`
 * today: cross-app SSO ships via the handoff broker (`./handoff.ts`) instead.
 * This iframe flow additionally CANNOT work in the current setup, because:
 *   1. `prompt=none` requires Cognito Managed Login branding (not enabled —
 *      the pool is on classic Hosted UI, which ignores the parameter), and
 *   2. the Cognito session cookie it reads only exists after an interactive
 *      login ON the auth domain — in-app email/password login (SRP) never
 *      creates it.
 * Known gap if ever revived: the PKCE `codeVerifier` generated below is not
 * returned to the caller and no token-exchange path exists.
 *
 * Original design (ADR-0001): when a user visits any product SPA, a hidden
 * iframe checks whether the user already has an active session on
 * auth.<stage>.gen-pro-hub.com. If they do, Cognito returns an auth code
 * without any visible UI. If not, it returns `login_required` and the
 * product shows its own login page.
 *
 * Security: tokens never touch the iframe — only the authorization code is
 * passed back via postMessage.
 */
import { getAuthConfig } from "./configure.js";
// =============================================================================
// Constants
// =============================================================================
const IFRAME_TIMEOUT_MS = 10_000;
const SILENT_CALLBACK_PATH = "/auth/silent-callback";
// =============================================================================
// PKCE helpers
// =============================================================================
function generateRandomString(length) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}
async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}
// =============================================================================
// Public API
// =============================================================================
/**
 * Attempt silent authentication by loading Cognito's authorize endpoint in a
 * hidden iframe with `prompt=none`.
 *
 * Returns the auth code on success, or `login_required` if no session exists.
 * The caller (AuthProvider) exchanges the code via Amplify's token endpoint.
 */
export async function attemptSilentAuth() {
    const config = getAuthConfig();
    const state = generateRandomString(16);
    const codeVerifier = generateRandomString(32);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    // Determine the silent callback URL (same origin as the current page)
    const silentCallbackUrl = `${window.location.origin}${SILENT_CALLBACK_PATH}`;
    const params = new URLSearchParams({
        client_id: config.userPoolClientId,
        response_type: "code",
        scope: (config.scopes ?? ["openid", "email", "profile"]).join(" "),
        redirect_uri: silentCallbackUrl,
        prompt: "none",
        state,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
    });
    const authorizeUrl = `https://${config.domain}/oauth2/authorize?${params.toString()}`;
    return new Promise((resolve) => {
        let resolved = false;
        const cleanup = () => {
            resolved = true;
            window.removeEventListener("message", onMessage);
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
            clearTimeout(timer);
        };
        // Listen for postMessage from the iframe's callback page
        const onMessage = (event) => {
            // Only accept messages from our own origin
            if (event.origin !== window.location.origin)
                return;
            if (!event.data || typeof event.data !== "object")
                return;
            const data = event.data;
            if (data.type !== "silent-auth-callback")
                return;
            cleanup();
            if (data.error === "login_required" || data.error === "consent_required") {
                resolve({ status: "login_required" });
            }
            else if (data.error) {
                resolve({ status: "error", error: String(data.error) });
            }
            else if (data.code && data.state === state) {
                resolve({
                    status: "authenticated",
                    code: String(data.code),
                    state: String(data.state),
                });
            }
            else {
                resolve({ status: "error", error: "Invalid response from silent auth iframe" });
            }
        };
        window.addEventListener("message", onMessage);
        // Create hidden iframe
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.setAttribute("aria-hidden", "true");
        iframe.src = authorizeUrl;
        document.body.appendChild(iframe);
        // Timeout — iframe may be blocked by browser or Cognito may not respond
        const timer = setTimeout(() => {
            if (!resolved) {
                cleanup();
                resolve({ status: "error", error: "Silent auth timed out" });
            }
        }, IFRAME_TIMEOUT_MS);
    });
}
/**
 * Minimal HTML for the /auth/silent-callback route.
 *
 * This page runs inside the hidden iframe. It reads the auth code (or error)
 * from the URL and posts it to the parent window. Products should serve this
 * at `/auth/silent-callback` — it can be a static HTML file or a tiny React
 * route that renders nothing visible.
 *
 * @example
 * ```tsx
 * // In your router:
 * <Route path="/auth/silent-callback" element={<SilentCallbackPage />} />
 *
 * // SilentCallbackPage.tsx:
 * import { handleSilentCallback } from "@d-m-organization/app-shell/auth";
 * export default function SilentCallbackPage() {
 *   React.useEffect(() => { handleSilentCallback(); }, []);
 *   return null;
 * }
 * ```
 */
export function handleSilentCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const message = { type: "silent-auth-callback" };
    if (error) {
        message.error = error;
    }
    else if (code) {
        message.code = code;
        message.state = state;
    }
    else {
        message.error = "no_code_or_error";
    }
    // Post to parent window (the product SPA that opened the iframe)
    if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, window.location.origin);
    }
}
